import React, { useState, useEffect } from 'react';
import AWS from 'aws-sdk';

// Configure AWS
AWS.config.update({
  region: process.env.REACT_APP_AWS_REGION,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
});

const dynamoDb = new AWS.DynamoDB.DocumentClient();
const ses = new AWS.SES();
const TABLE_NAME = 'storage-management';
// New dedicated table for waiting-list notification recipients
const WAITING_LIST_RECIPIENTS_TABLE = 'waiting-list-recipients';
const FROM_EMAIL = process.env.REACT_APP_FROM_EMAIL || 'no-reply@storehere.com.au';

// Temporary override: always show containers as available on homepage
// Set to false to restore real availability check
const FORCE_SHOW_CONTAINERS_AVAILABLE = true;

// Container operations
const getContainers = async (siteId) => {
  const params = {
    TableName: TABLE_NAME,
    KeyConditionExpression: 'PK = :pk AND begins_with(SK, :sk)',
    ExpressionAttributeValues: {
      ':pk': `SITE#${siteId}`,
      ':sk': 'CONTAINER#'
    }
  };
  
  try {
    const result = await dynamoDb.query(params).promise();
    return result.Items;
  } catch (error) {
    console.error('Error getting containers:', error);
    throw error;
  }
};

// Notify backend to email waiting-list recipients
const notifyWaitingListBackend = async (customerEmail) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}/waiting-list-notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ email: customerEmail }).toString()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
};

const HomePage = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState(0);
  const [containersAvailable, setContainersAvailable] = useState(false);
  const [loadingContainers, setLoadingContainers] = useState(true);
  const [waitingListEmail, setWaitingListEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState(''); // 'loading', 'success', 'error'
  const [showContactModal, setShowContactModal] = useState(false);
  
  const S3_BUCKET_URL = 'https://storehere-agreements.s3.amazonaws.com';
  
  const galleryImages = [
    `/Pic 1.jpg`,
    `/Pic 5.jpg`
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % galleryImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [galleryImages.length]);

  useEffect(() => {
    setIsVisible(true);
    checkContainerAvailability();
  }, []);

  const checkContainerAvailability = async () => {
    // If override enabled, force UI to show availability and skip querying DynamoDB
    if (FORCE_SHOW_CONTAINERS_AVAILABLE) {
      setContainersAvailable(true);
      setLoadingContainers(false);
      return;
    }
    try {
      // Using 'edwardstown' as the site ID based on the company location
      const containers = await getContainers('edwardstown');
      const availableContainers = containers.filter(container => container.status === 'available');
      setContainersAvailable(availableContainers.length > 0);
    } catch (error) {
      console.error('Error checking container availability:', error);
      setContainersAvailable(false);
    } finally {
      setLoadingContainers(false);
    }
  };

  const handleWaitingListSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');
    
    try {
      // Notify staff via backend
      await notifyWaitingListBackend(waitingListEmail);
      
      setSubmitStatus('success');
      setWaitingListEmail('');
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      console.error('Error joining waiting list:', error);
      setSubmitStatus('error');
      
      // Reset error message after 5 seconds
      setTimeout(() => setSubmitStatus(''), 5000);
    }
  };

  const faqs = [
    {
      question: "Container Dimensions?",
      answer: "Our 20ft shipping containers are 2.6m(H) × 2.4m(W) × 6.0m(L) - perfect for most storage needs."
    },
    {
      question: "Short Term Storage?",
      answer: "Stay as long or as short as you like! No lock-in contracts. Call 0408 805 996 to check availability."
    },
    {
      question: "How Much Does It Cost?",
      answer: "$85 per week (inc GST) plus $300 refundable security bond. Payment required before access."
    },
    {
      question: "Payment Options?",
      answer: "Direct Debit via GoCardless. Choose weekly or monthly payments - always paid in advance."
    },
    {
      question: "ID Requirements?",
      answer: "Photo ID required. Must sign terms and conditions. Credit check may apply."
    },
    {
      question: "Vehicle Storage?",
      answer: "No vehicle storage available."
    },
    {
      question: "Location?",
      answer: "Ivanhoe Ave Edwardstown - right behind Bunnings!"
    },
    {
      question: "Contents Insurance?",
      answer: "You're responsible for insuring your contents. We don't provide coverage."
    },
    {
      question: "Security Features?",
      answer: "Multiple security cameras, fully fenced and locked site, individual container locks."
    },
    {
      question: "Moving Equipment?",
      answer: "We don't supply trolleys, boxes, or moving equipment."
    },
    {
      question: "Shelving Allowed?",
      answer: "Free-standing shelving is fine. No permanent fixtures that damage containers."
    },
    {
      question: "Waterproof?",
      answer: "Yes, containers are fully waterproof."
    }
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col touch-manipulation">
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.6s ease-out;
        }
        
        /* Improve mobile touch targets */
        @media (max-width: 768px) {
          button, a, input[type="submit"] {
            min-height: 44px !important;
            min-width: 44px !important;
          }
          
          /* Ensure gallery navigation is touch-friendly */
          .gallery-nav button {
            min-height: 48px !important;
            min-width: 48px !important;
            padding: 12px 16px !important;
          }
          
          /* Larger touch targets for image dots */
          .image-dot {
            min-height: 44px !important;
            min-width: 44px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
        }
        
        /* Smooth scrolling for mobile */
        html {
          scroll-behavior: smooth;
        }
        
        /* Prevent zoom on iOS form inputs */
        @media screen and (max-width: 767px) {
          input[type="email"] {
            font-size: 16px !important;
          }
        }
        
        /* Better mobile header spacing */
        @media (max-width: 640px) {
          .mobile-header {
            padding-left: 8px !important;
            padding-right: 8px !important;
          }
          
          .mobile-logo {
            max-width: 60% !important;
            height: auto !important;
          }
        }
      `}</style>
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto mobile-header px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-[auto,1fr,auto] items-center h-14 sm:h-20 lg:h-24 gap-3">
            {/* Left: Logo (icon on mobile, full on larger screens) */}
            <div className="flex items-center">
              {/* Icon-only mark for mobile */}
              <img
                src="/Logo.png"
                alt="StoreHere"
                className="h-9 w-auto sm:hidden"
              />
              {/* Full landscape logo for >= sm */}
              <img 
                src={`${S3_BUCKET_URL}/photos/White Landscape Logo.jpg`} 
                alt="StoreHere Self Storage" 
                className="hidden sm:block h-10 sm:h-16 lg:h-20 w-auto"
              />
            </div>

            {/* Center: Phone number (hidden on mobile) */}
            <div className="hidden sm:flex items-center justify-center h-full">
              <a 
                href="tel:0408805996" 
                className="text-orange-600 font-extrabold tracking-wide leading-tight whitespace-nowrap text-[clamp(1rem,6vw,1.75rem)] sm:text-2xl lg:text-3xl"
              >
                0408 805 996
              </a>
            </div>
            
            {/* Right: Auth + CTA (compact on mobile) */}
            <div className="flex items-center justify-end gap-2 sm:gap-3 lg:gap-6">
              <a 
                href="/signin" 
                className="text-gray-700 hover:text-orange-500 px-2 sm:px-4 lg:px-6 py-2 sm:py-3 text-[clamp(0.875rem,3.5vw,1rem)] sm:text-base lg:text-lg font-medium transition-all duration-300 hover:bg-gray-50 rounded-lg min-h-[40px] sm:min-h-[44px] flex items-center justify-center"
              >
                Login
              </a>
              {/* Mobile: smaller CTA opens contact modal */}
              <button 
                type="button"
                onClick={() => setShowContactModal(true)}
                className="inline-flex sm:hidden items-center justify-center bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-full text-sm font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg"
              >
                Book
              </button>
              {/* ≥ sm: larger CTA opens contact modal */}
              <button 
                type="button"
                onClick={() => setShowContactModal(true)}
                className="hidden sm:inline-flex bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-8 lg:px-12 py-2.5 sm:py-3.5 rounded-full text-base sm:text-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-2xl hover:shadow-3xl"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 bg-gradient-to-br from-orange-50 via-white to-gray-50">
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 transition-opacity duration-1000">
            <img 
              src={galleryImages[currentImage]} 
              alt="Storage Containers" 
              className="w-full h-full object-cover"
            />
            {/* Balanced overlay: moderate lighten with slight bottom contrast */}
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/10 to-black/25"></div>
          </div>
          
          <div className={`relative z-10 text-center px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="relative inline-block text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-black mb-4 sm:mb-6 leading-none tracking-tight">
              <span
                aria-hidden="true"
                className="absolute inset-0 text-transparent"
                style={{ WebkitTextStroke: '4px #f97316' }}
              >
                STORAGE
              </span>
              <span className="relative text-white">STORAGE</span>
            </h1>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold mb-6 sm:mb-8 text-orange-500">
              MADE EASY
            </h2>
            
            {/* Replace dots with price text */}
            <div className="mb-6 sm:mb-8">
              <div className="text-white text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight drop-shadow-md">$85</div>
            </div>
            
            <button 
              type="button"
              onClick={() => setShowContactModal(true)}
              className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 sm:px-10 lg:px-12 py-3 sm:py-4 rounded-full text-lg sm:text-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:scale-105"
            >
              Book Now
            </button>
          </div>
        </section>

        {/* Information Wrapper with wide, very faded orange gradient */}
        <div className="bg-gradient-to-b from-orange-500/10 via-orange-400/5 to-orange-500/10">

        {/* Pricing Section */}
        <section className="py-8 sm:py-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 max-w-2xl mx-auto border-2 border-orange-400 shadow-2xl">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-orange-500 mb-3 sm:mb-4">$85</div>
              <div className="text-xl sm:text-2xl text-gray-800 font-semibold mb-6 sm:mb-8">per week (inc GST)</div>
              <div className="text-gray-700 space-y-2 sm:space-y-3 text-base sm:text-lg font-medium">
                <p>20ft Shipping Containers • 24/7 Access • No Lock-in Contract</p>
                <p>2.6m(H) × 2.4m(W) × 6.0m(L)</p>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 lg:py-16">
            {/* Gallery Navigation */}
            <div className="flex justify-center mb-6 sm:mb-8 overflow-x-auto px-4">
              <div className="bg-white/80 backdrop-blur-sm p-1 sm:p-1.5 rounded-xl sm:rounded-2xl border border-gray-200 shadow-lg">
                <div className="flex space-x-1 min-w-max">
                  {[
                    { id: 0, title: "Why Choose Us", shortTitle: "Why Us" },
                    { id: 1, title: "Get Started", shortTitle: "Start" },
                    { id: 2, title: "FAQ", shortTitle: "FAQ" },
                    { id: 3, title: "Contact", shortTitle: "Contact" }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveSection(tab.id)}
                      className={`gallery-nav whitespace-nowrap px-3 sm:px-4 lg:px-5 py-2.5 sm:py-2.5 rounded-lg sm:rounded-xl text-sm sm:text-base lg:text-lg font-semibold transition-all duration-300 min-h-[44px] flex items-center justify-center ${
                        activeSection === tab.id
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                          : 'text-gray-600 hover:text-orange-500 hover:bg-gray-50'
                      }`}
                    >
                      <span className="block sm:hidden">{tab.shortTitle}</span>
                      <span className="hidden sm:block">{tab.title}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Gallery Content */}
            <div className="relative">
              {/* Why Choose Us */}
              {activeSection === 0 && (
                <div className="w-full animate-fadeIn">
                  <div className="text-center mb-6 sm:mb-8">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent">
                      Why Choose Us?
                    </h2>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {[
                      {
                        title: "CONVENIENT LOCATION",
                        subtitle: "Right behind Bunnings Edwardstown"
                      },
                      {
                        title: "24/7 ACCESS",
                        subtitle: "Unrestricted access 365 days a year"
                      },
                      {
                        title: "SECURE FACILITY",
                        subtitle: "Gated with cameras and flood lighting"
                      }
                    ].map((feature, index) => (
                      <div key={index} className="group">
                        <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-200 hover:border-orange-300 transition-all duration-500 hover:shadow-2xl transform hover:scale-105 h-full text-center">
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{feature.title}</h3>
                          <p className="text-base sm:text-lg text-gray-600">{feature.subtitle}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Get Started */}
              {activeSection === 1 && (
                <div className="w-full animate-fadeIn">
                  <div className="text-center mb-8 sm:mb-12">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent">
                      Get Started in 5 Steps
                    </h2>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {[
                      { step: "01", title: "Check Availability", desc: "Look below for container availability\n\nJoin our waiting list if no containers are currently available" },
                      { step: "02", title: "Sign Up", desc: "Create an account to sign up for your storage container" },
                      { step: "03", title: "Payment", desc: "Set up your payment method for storage fees" },
                      { step: "04", title: "Visit Our Office", desc: "Visit our office to provide photo ID\n16-26 Weaver Street Edwardstown, SA\nMon-Fri 7.30am – 4.30pm" },
                      { step: "05", title: "Start Storing", desc: "We’ll provide you with access instructions on site" }
                    ].map((item, index) => (
                      <div key={index} className="group text-center">
                        <div className="bg-gradient-to-br from-orange-500 to-orange-600 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-5 text-lg sm:text-xl font-bold text-white group-hover:scale-110 transition-transform duration-300 shadow-lg">
                          {item.step}
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1.5 sm:mb-2.5">{item.title}</h3>
                        <p className="text-sm sm:text-base text-gray-600 whitespace-pre-line">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ */}
              {activeSection === 2 && (
                <div className="w-full animate-fadeIn">
                  <div className="text-center mb-6 sm:mb-8">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent">
                      Frequently Asked Questions
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-600">
                      Need answers? Call us on <span className="text-orange-500 font-semibold">0408 805 996</span>
                    </p>
                  </div>
                  
                  <div className="max-w-4xl mx-auto">
                    <div className="grid gap-4 sm:gap-6 lg:grid-cols-2">
                      {faqs.map((faq, index) => (
                        <div key={index} className="group">
                          <div className="bg-white/80 backdrop-blur-sm p-3.5 sm:p-5 rounded-xl sm:rounded-2xl border border-gray-200 hover:border-orange-300 transition-all duration-300 hover:shadow-lg h-full">
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2 sm:mb-3 group-hover:text-orange-500 transition-colors duration-300">
                              {faq.question}
                            </h3>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{faq.answer}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact */}
              {activeSection === 3 && (
                <div className="w-full animate-fadeIn">
                  <div className="text-center mb-6 sm:mb-8">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent">
                      Contact Us
                    </h2>
                  </div>
                  
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {[
                      { 
                        title: "Address", 
                        info: "14 Weaver St, Edwardstown SA 5039"
                      },
                      { 
                        title: "Phone", 
                        info: "0408 805 996"
                      },
                      { 
                        title: "Email", 
                        info: "storage@storehere.com.au"
                      }
                    ].map((contact, index) => (
                      <div key={index} className="group">
                        <div className="bg-white/80 backdrop-blur-sm p-4 sm:p-6 rounded-2xl sm:rounded-3xl border border-gray-200 hover:border-orange-300 transition-all duration-500 hover:shadow-2xl transform hover:scale-105 h-full text-center">
                          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{contact.title}</h3>
                          <p className="text-sm sm:text-base text-gray-600">{contact.info}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="text-center mt-6 sm:mt-8">
                    <div className="text-gray-500 text-xs sm:text-sm space-y-1 sm:space-y-2">
                      <p>Short or long term storage solutions for commercial or residential storage</p>
                      <p>©2022 by Store Here</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Container Availability Section */}
        <section className="py-10 sm:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            {loadingContainers ? (
              <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-gray-200 shadow-lg">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent mb-4">
                  Checking Availability
                </h3>
                <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : containersAvailable ? (
              <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-gray-200 shadow-lg">
                <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent mb-4">
                  Containers Available
                </h3>
                <p className="text-lg sm:text-xl text-gray-600 mb-5 sm:mb-6">
                  Great news! We have storage containers available right now. 
                  Get started with your storage solution today.
                </p>
                <button 
                  type="button"
                  onClick={() => setShowContactModal(true)}
                  className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-7 sm:px-8 py-3 sm:py-3.5 rounded-full text-lg sm:text-xl font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  Book a Container
                </button>
              </div>
            ) : (
              <div className="bg-white/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl sm:rounded-3xl border border-gray-200 shadow-lg">
                <h3 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-orange-600 to-gray-700 bg-clip-text text-transparent mb-4">
                  Currently Full
                </h3>
                <p className="text-lg sm:text-xl text-gray-600 mb-5 sm:mb-6">
                  All containers are currently occupied. Join our waiting list to be notified 
                  when containers become available. We get notified when you join our waiting list and contact quickly to get you a container.
                </p>
                <form onSubmit={handleWaitingListSubmit} className="max-w-md mx-auto">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <input
                      type="email"
                      value={waitingListEmail}
                      onChange={(e) => setWaitingListEmail(e.target.value)}
                      placeholder="Enter your email address"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-base sm:text-lg min-h-[44px]"
                      required
                      disabled={submitStatus === 'loading'}
                    />
                    <button
                      type="submit"
                      disabled={submitStatus === 'loading'}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 sm:px-7 py-3 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none min-h-[44px] flex items-center justify-center whitespace-nowrap"
                    >
                      {submitStatus === 'loading' ? 'Joining...' : 'Join List'}
                    </button>
                  </div>
                  
                  {submitStatus === 'success' && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm">
                        Thank you! We've notified our team. We'll contact you soon.
                      </p>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-red-800 text-sm">
                        Sorry, there was an error submitting your request. Please try again.
                      </p>
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
        </section>

        </div>{/* end gradient wrapper */}
  </main>

      {/* Contact Modal */}
      {showContactModal && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowContactModal(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative bg-white w-full max-w-md sm:max-w-lg rounded-2xl shadow-2xl border border-orange-200 p-6 sm:p-8 z-[80]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close"
              onClick={() => setShowContactModal(false)}
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path fillRule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clipRule="evenodd" />
              </svg>
            </button>

            <div className="text-center">
              <div className="mb-6">
                <img src="/Logo.png" alt="StoreHere" className="h-12 sm:h-14 w-auto mx-auto" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-4">Containers Available</h3>
              <a href="tel:0408805996" className="block font-black text-orange-600 text-4xl sm:text-6xl leading-tight">
                0408 805 996
              </a>
              <a href="mailto:storage@storehere.com.au" className="mt-3 block text-lg sm:text-xl font-semibold text-gray-800 break-words">
                storage@storehere.com.au
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="relative w-full bg-black">
        <div className="w-full">
          <img 
            src={`${S3_BUCKET_URL}/photos/Black Background Header panel.jpg`} 
            alt="StoreHere Footer" 
            className="w-full h-auto object-cover object-center min-h-[80px] sm:min-h-[100px]"
          />
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
